import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Award, Gift, Settings, Users, TrendingUp, Ticket, Plus, Edit, Trash2, Loader2, BarChart3, Save } from 'lucide-react';

export default function AdminPoints() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pointsConfig, setPointsConfig] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('config');
  const [rewardDialogOpen, setRewardDialogOpen] = useState(false);
  const [editingReward, setEditingReward] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const [rewardForm, setRewardForm] = useState({ name: '', description: '', points_required: 100, discount_percent: 10, discount_code_prefix: 'SPICY', max_uses: 1, valid_days: 30, is_active: true });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [configRes, rewardsRes, statsRes] = await Promise.all([
        api.get('/admin/points/config'),
        api.get('/admin/points/rewards'),
        api.get('/admin/points/stats')
      ]);
      setPointsConfig(configRes.data);
      setRewards(rewardsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/home');
      return;
    }
    loadData();
  }, [user, navigate, loadData]);

  const handleConfigChange = (action, field, value) => {
    setPointsConfig(prev => prev.map(c => c.action === action ? { ...c, [field]: value } : c));
  };

  const saveConfig = async (config) => {
    setSavingConfig(true);
    try {
      await api.put('/admin/points/config/' + config.action, { action: config.action, points: config.points, description: config.description, is_active: config.is_active });
      toast.success('Configuração guardada');
    } catch (error) {
      toast.error('Erro ao guardar');
    } finally {
      setSavingConfig(false);
    }
  };

  const openRewardDialog = (reward = null) => {
    if (reward) {
      setEditingReward(reward);
      setRewardForm(reward);
    } else {
      setEditingReward(null);
      setRewardForm({ name: '', description: '', points_required: 100, discount_percent: 10, discount_code_prefix: 'SPICY', max_uses: 1, valid_days: 30, is_active: true });
    }
    setRewardDialogOpen(true);
  };

  const saveReward = async () => {
    try {
      if (editingReward) {
        await api.put('/admin/points/rewards/' + editingReward.id, rewardForm);
        toast.success('Recompensa atualizada');
      } else {
        await api.post('/admin/points/rewards', rewardForm);
        toast.success('Recompensa criada');
      }
      setRewardDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Erro ao guardar recompensa');
    }
  };

  const deleteReward = async (id) => {
    if (!confirm('Eliminar esta recompensa?')) return;
    try {
      await api.delete('/admin/points/rewards/' + id);
      toast.success('Recompensa eliminada');
      loadData();
    } catch (error) {
      toast.error('Erro ao eliminar');
    }
  };

  const seedRewards = async () => {
    try {
      const res = await api.post('/admin/points/seed-rewards');
      toast.success(res.data.message);
      loadData();
    } catch (error) {
      toast.error('Erro ao criar recompensas');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-ruby" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="max-w-7xl mx-auto px-4 pt-24 pb-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
              <Award className="w-8 h-8 text-ruby" />
              Gestão de Pontos
            </h1>
            <p className="text-muted-foreground mt-1">Configura o sistema de pontos e recompensas</p>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="bg-surface border-border">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Pontos Atribuídos</p>
                    <p className="text-2xl font-bold text-foreground">{stats.total_points_awarded}</p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-green-400" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-surface border-border">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Pontos Resgatados</p>
                    <p className="text-2xl font-bold text-ruby">{stats.total_points_redeemed}</p>
                  </div>
                  <Gift className="w-8 h-8 text-ruby" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-surface border-border">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Códigos Gerados</p>
                    <p className="text-2xl font-bold text-foreground">{stats.total_discounts_generated}</p>
                  </div>
                  <Ticket className="w-8 h-8 text-blue-400" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-surface border-border">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Códigos Utilizados</p>
                    <p className="text-2xl font-bold text-foreground">{stats.discounts_used}</p>
                  </div>
                  <BarChart3 className="w-8 h-8 text-purple-400" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button variant={activeTab === 'config' ? 'default' : 'outline'} onClick={() => setActiveTab('config')} className={activeTab === 'config' ? 'bg-ruby' : 'border-border'}>
            <Settings className="w-4 h-4 mr-2" />
            Configuração de Pontos
          </Button>
          <Button variant={activeTab === 'rewards' ? 'default' : 'outline'} onClick={() => setActiveTab('rewards')} className={activeTab === 'rewards' ? 'bg-ruby' : 'border-border'}>
            <Gift className="w-4 h-4 mr-2" />
            Recompensas
          </Button>
          <Button variant={activeTab === 'users' ? 'default' : 'outline'} onClick={() => setActiveTab('users')} className={activeTab === 'users' ? 'bg-ruby' : 'border-border'}>
            <Users className="w-4 h-4 mr-2" />
            Top Utilizadores
          </Button>
        </div>

        {/* Config Tab */}
        {activeTab === 'config' && (
          <Card className="bg-surface border-border">
            <CardHeader>
              <CardTitle className="text-foreground">Pontos por Ação</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {pointsConfig.map((config) => (
                  <div key={config.action} className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                    <div className="flex items-center gap-4 flex-1">
                      <Switch checked={config.is_active} onCheckedChange={(v) => handleConfigChange(config.action, 'is_active', v)} />
                      <div className="flex-1">
                        <p className="font-medium text-foreground">{config.description}</p>
                        <p className="text-sm text-muted-foreground">{config.action}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Input type="number" value={config.points} onChange={(e) => handleConfigChange(config.action, 'points', parseInt(e.target.value))} className="w-24 bg-background border-border text-center" />
                      <span className="text-muted-foreground">pontos</span>
                      <Button size="sm" onClick={() => saveConfig(config)} disabled={savingConfig} className="bg-ruby hover:bg-ruby-light">
                        <Save className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Rewards Tab */}
        {activeTab === 'rewards' && (
          <div>
            <div className="flex justify-between mb-4">
              <Button onClick={() => openRewardDialog()} className="bg-ruby hover:bg-ruby-light">
                <Plus className="w-4 h-4 mr-2" />
                Nova Recompensa
              </Button>
              <Button variant="outline" onClick={seedRewards} className="border-border">
                Criar Recompensas de Exemplo
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {rewards.map((reward) => (
                <Card key={reward.id} className={'bg-surface border-border ' + (!reward.is_active ? 'opacity-60' : '')}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-medium text-foreground">{reward.name}</h3>
                        <p className="text-sm text-muted-foreground">{reward.description}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button size="icon" variant="ghost" onClick={() => openRewardDialog(reward)}><Edit className="w-4 h-4" /></Button>
                        <Button size="icon" variant="ghost" className="text-red-400" onClick={() => deleteReward(reward.id)}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Badge className="bg-ruby">{reward.discount_percent}% OFF</Badge>
                      <Badge variant="outline" className="border-border">{reward.points_required} pontos</Badge>
                      <Badge variant="outline" className="border-border">{reward.valid_days} dias</Badge>
                      {!reward.is_active && <Badge variant="outline" className="border-red-500 text-red-400">Inativo</Badge>}
                    </div>
                  </CardContent>
                </Card>
              ))}
              {rewards.length === 0 && (
                <div className="col-span-full text-center py-12">
                  <Gift className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">Nenhuma recompensa configurada</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Top Users Tab */}
        {activeTab === 'users' && stats?.top_users && (
          <Card className="bg-surface border-border">
            <CardHeader>
              <CardTitle className="text-foreground">Top 10 Utilizadores por Pontos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {stats.top_users.map((u, index) => (
                  <div key={u.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className={'w-8 h-8 rounded-full flex items-center justify-center font-bold ' + (index < 3 ? 'bg-ruby text-white' : 'bg-white/10 text-muted-foreground')}>
                        {index + 1}
                      </span>
                      <div>
                        <p className="font-medium text-foreground">{u.name}</p>
                        <p className="text-sm text-muted-foreground">{u.email}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-ruby">{u.points} pts</p>
                      <p className="text-xs text-muted-foreground">Total: {u.lifetime_points}</p>
                    </div>
                  </div>
                ))}
                {stats.top_users.length === 0 && (
                  <p className="text-center text-muted-foreground py-8">Nenhum utilizador com pontos</p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Reward Dialog */}
        <Dialog open={rewardDialogOpen} onOpenChange={setRewardDialogOpen}>
          <DialogContent className="bg-surface border-border">
            <DialogHeader>
              <DialogTitle className="text-foreground">{editingReward ? 'Editar Recompensa' : 'Nova Recompensa'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Nome</Label>
                <Input value={rewardForm.name} onChange={(e) => setRewardForm({ ...rewardForm, name: e.target.value })} className="bg-background border-border" placeholder="10% Desconto Pimenta Doce" />
              </div>
              <div className="space-y-2">
                <Label>Descrição</Label>
                <Textarea value={rewardForm.description} onChange={(e) => setRewardForm({ ...rewardForm, description: e.target.value })} className="bg-background border-border" placeholder="Código válido em toda a loja..." />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Pontos Necessários</Label>
                  <Input type="number" value={rewardForm.points_required} onChange={(e) => setRewardForm({ ...rewardForm, points_required: parseInt(e.target.value) })} className="bg-background border-border" />
                </div>
                <div className="space-y-2">
                  <Label>% Desconto</Label>
                  <Input type="number" value={rewardForm.discount_percent} onChange={(e) => setRewardForm({ ...rewardForm, discount_percent: parseInt(e.target.value) })} className="bg-background border-border" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Prefixo do Código</Label>
                  <Input value={rewardForm.discount_code_prefix} onChange={(e) => setRewardForm({ ...rewardForm, discount_code_prefix: e.target.value })} className="bg-background border-border" placeholder="SPICY10" />
                </div>
                <div className="space-y-2">
                  <Label>Validade (dias)</Label>
                  <Input type="number" value={rewardForm.valid_days} onChange={(e) => setRewardForm({ ...rewardForm, valid_days: parseInt(e.target.value) })} className="bg-background border-border" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={rewardForm.is_active} onCheckedChange={(v) => setRewardForm({ ...rewardForm, is_active: v })} />
                <Label>Ativa</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRewardDialogOpen(false)} className="border-border">Cancelar</Button>
              <Button onClick={saveReward} className="bg-ruby hover:bg-ruby-light">Guardar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
